import { css } from "lit";
import { customElement, property } from "lit/decorators.js";
import { msg } from "@omegagrid/localize";
import { dom, MenuItem } from "@omegagrid/core";
import { Editor } from "@omegagrid/editor";
import { GridEditEvent, GridModel, GridSource, GroupModelSource, RowModel, ColumnModel } from "@omegagrid/grid";
import { TabEvent } from "@omegagrid/tabs";
import { createWorkbook, exportXLSX } from "@omegagrid/plugin-xlsx";
import rp from "@reporting/rp-app-host";
import { MultiContextData } from "@reporting/rp-types/analytics";
import { BaseVisualization, ReportEvent, VisualizationOptions } from "./baseVisualization";
import { SpreadsheetAdapter, SpreadsheetItemSource } from "./spreadsheetAdapter";
import { ReportChangeEvent, ReportStore } from "../../model/reportStore";
import { UidMultiContextDataItem } from "../../model/itemUid";
import { createCustomName, createSheetDiff, CUSTOM_PROP, sanitize } from "./sheetDiff";

/** delay between the last grid edit and the store commit of the sheet */
const COMMIT_DEBOUNCE = 400;

const ERROR_TAB_COLOR = 'D9534F';

type ListSelectEvent = Event & {index: number};

@customElement('rp-visual-spreadsheet')
export class SpreadsheetVisualization extends BaseVisualization<MultiContextData> {

	static styles = [css`
		:host {
			display: block;
			width: 100%;
			height: 100%;
			position: relative;
		}

		og-editor {
			width: 100%;
			height: 100%;
		}
	`];

	@property({type: Object})
	adapter: SpreadsheetAdapter;

	/** created imperatively once and kept across lit renders - og-editor loses all state on recreation */
	private _editor: Editor;

	/** uid → pristine named server grid (pre-diff base of data sheets) */
	private _baseSources = new Map<string, GridSource>();
	/** sheet name → item uid */
	private _uidByName = new Map<string, string>();
	/** uid → item source of the last load */
	private _itemSources = new Map<string, SpreadsheetItemSource>();
	/** uid → last rendered item snapshot (reference comparison thanks to immer structural sharing) */
	private _renderedItems = new Map<string, UidMultiContextDataItem>();
	/** last rendered common filters snapshot */
	private _renderedFilters: unknown;
	/** uid → pending debounced commit */
	private _pendingCommits = new Map<string, ReturnType<typeof setTimeout>>();
	/** suppress the store echo of our own commits */
	private _committing = false;

	get store(): ReportStore {
		return this.adapter?.store ?? null;
	}

	private _getEditor() {
		if (this._editor) return this._editor;
		const editor = this._editor = dom.createElement<Editor>('og-editor');
		editor.addEventListener('grid.edit', this._onGridEdit);
		editor.addEventListener('tab.add', this._onTabAdd);
		editor.addEventListener('tab.move', this._onTabMove);
		editor.addEventListener('tab.contextmenu', this._onTabContextMenu, {capture: true});
		return editor;
	}

	render = () => this._getEditor();

	layout() {
		this._editor?.layout();
	}

	async updated(props: Map<string, any>) {
		super.updated(props);
		if (props.has('adapter')) {
			const previous = props.get('adapter') as SpreadsheetAdapter;
			previous?.store?.removeEventListener('change', this._onStoreChange);
			this.store?.addEventListener('change', this._onStoreChange);
			await this.loadData();
		}
	}

	connectedCallback() {
		super.connectedCallback();
		this.store?.addEventListener('change', this._onStoreChange);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.store?.removeEventListener('change', this._onStoreChange);
	}

	/** 1-cell sheet shown when the report data of a sheet failed to load */
	private createErrorSource(item: SpreadsheetItemSource, error: unknown): GridSource {
		rp.log.error(error);
		return {
			name: item.name,
			props: {uid: item.uid, error: true},
			cells: [{r: 0, c: 0, v: `${msg('Error')}: ${(error as Error)?.message ?? error}`, t: 't'}],
			tab: {color: ERROR_TAB_COLOR},
			options: {editable: false},
		};
	}

	/** og-editor layout from the persisted view options, when its tab ids match the current sheets */
	private resolveLayout(names: string[]): GroupModelSource['layout'] {
		const layout = this.adapter.viewOptions?.layout as GroupModelSource['layout'];
		const collectIds = (container: {items?: {tabs?: {id?: unknown}[], container?: unknown}[]}): string[] =>
			(container?.items ?? []).flatMap(item => [
				...(item.tabs ?? []).map(tab => tab.id as string),
				...collectIds(item.container as never ?? {}),
			]);

		if (layout) {
			const ids = collectIds(layout as never);
			const nameSet = new Set(names);
			if (ids.length === names.length && ids.every(id => nameSet.has(id))) return sanitize(layout);
		}

		return {items: [{size: null, tabs: names.map(name => ({id: name, title: name}))}]};
	}

	async loadData() {
		if (!this.adapter) return;
		this.commitEdits();

		const items = this.adapter.getItems();
		const sources = await Promise.allSettled(items.map(item => this.adapter.getSheetSource(item)));

		this._baseSources.clear();
		this._uidByName.clear();
		this._itemSources.clear();

		const models: GridSource[] = sources.map((result, i) => {
			const item = items[i];
			this._itemSources.set(item.uid, item);
			if (result.status === 'fulfilled') {
				if (result.value.base) this._baseSources.set(item.uid, result.value.base);
				return result.value.source;
			}
			return this.createErrorSource(item, result.reason);
		});
		models.forEach((model, i) => {
			model.index = i;
			this._uidByName.set(model.name, items[i].uid);
		});

		this.syncRenderedItems();

		const editor = this._getEditor();
		editor.setSourceData({
			models: models,
			layout: this.resolveLayout(models.map(m => m.name)),
		});

		this.runScripts();
		this.dispatchEvent(new ReportEvent('load', {visualization: this}));
	}

	refresh(contextData?: MultiContextData) {
		super.refresh(contextData);
		if (contextData) this.loadData();
	}

	private syncRenderedItems() {
		this._renderedItems.clear();
		this.store?.items.forEach(item => this._renderedItems.set(item.uid, item));
		this._renderedFilters = (this.store?.contextData as MultiContextData)?.filters;
	}

	private _onStoreChange = async (e: Event) => {
		const scope = (e as ReportChangeEvent).scope;
		this.contextData = this.adapter.contextData;
		if (this._committing) {
			this.syncRenderedItems();
			return;
		}

		switch (scope) {
			case 'structure':
			case 'reset':
				await this.loadData();
				break;
			case 'filters': {
				// common filters changed → all data sheets are stale; an item filter
				// change is detected by the item reference
				const filtersChanged = (this.store.contextData as MultiContextData)?.filters !== this._renderedFilters;
				const changed = this.changedItems();
				this.syncRenderedItems();
				for (const item of this.adapter.getItems()) {
					if (item.freeForm) continue;
					if (filtersChanged || changed.has(item.uid)) await this.reloadSheet(item);
				}
				break;
			}
			case 'content': {
				// external edit (sidebar rename, discard of a single item, ...)
				const changed = this.changedItems();
				this.syncRenderedItems();
				for (const item of this.adapter.getItems()) {
					if (!changed.has(item.uid)) continue;
					this.renameSheetIfNeeded(item);
				}
				break;
			}
		}
	}

	/** uids of items whose store reference changed since the last render */
	private changedItems(): Set<string> {
		const changed = new Set<string>();
		this.store?.items.forEach(item => {
			if (this._renderedItems.get(item.uid) !== item) changed.add(item.uid);
		});
		return changed;
	}

	private sheetNameOf(uid: string): string {
		for (const [name, u] of this._uidByName) {
			if (u === uid) return name;
		}
		return null;
	}

	private renameSheetIfNeeded(item: SpreadsheetItemSource | UidMultiContextDataItem) {
		const uid = (item as SpreadsheetItemSource).uid ?? (item as UidMultiContextDataItem).uid;
		const current = this.sheetNameOf(uid);
		const next = this.adapter.getItems().find(i => i.uid === uid)?.name;
		if (!current || !next || current === next) return;
		try {
			this._editor.gridContainer.renameSheet(current, next);
			this._uidByName.delete(current);
			this._uidByName.set(next, uid);
		} catch (error) {
			rp.log.error(error);
		}
	}

	/** re-fetch the report data of one sheet and repopulate its model in place (legacy refreshItem pattern) */
	private async reloadSheet(item: SpreadsheetItemSource) {
		const name = this.sheetNameOf(item.uid) ?? item.name;
		const model = this._editor.gridContainer.model.get(name);
		if (!model) return;

		try {
			const {source, base} = await this.adapter.getSheetSource({...item, name});
			if (base) this._baseSources.set(item.uid, base);
			model.populate(source);
			this._editor.gridContainer.grids.get(name)?.rendering.renderAll(true);
		} catch (error) {
			rp.log.error(error);
		}
	}

	// -- edit capture ---------------------------------------------------------

	private _onGridEdit = (e: Event) => {
		const event = e as GridEditEvent;
		const model = event.grid?.model;
		if (!model) return;
		const uid = this._uidByName.get(model.name);
		if (!uid) return;

		if (event.editType === 'insert_row' || event.editType === 'insert_column') {
			this.nameInsertedElements(event);
		}

		clearTimeout(this._pendingCommits.get(uid));
		this._pendingCommits.set(uid, setTimeout(() => {
			this._pendingCommits.delete(uid);
			this.commitSheet(uid);
		}, COMMIT_DEBOUNCE));
	}

	/** give freshly inserted rows/columns a stable name + custom marker (diff detection) */
	private nameInsertedElements(event: GridEditEvent) {
		const elements = new Set<RowModel | ColumnModel>();
		if (event.row) elements.add(event.row);
		if (event.column) elements.add(event.column);
		event.cells?.forEach(cell => {
			if (event.editType === 'insert_row' && cell.row) elements.add(cell.row);
			if (event.editType === 'insert_column' && cell.column) elements.add(cell.column);
		});
		elements.forEach(element => {
			if (!element.name) element.name = createCustomName();
			if (!element.getProperty?.(CUSTOM_PROP)) element.setProperty?.(CUSTOM_PROP, true);
		});
	}

	/** serialize the sheet into the store: diff for data sheets, full model for free-form sheets */
	private commitSheet(uid: string) {
		const name = this.sheetNameOf(uid);
		const model = name ? this._editor.gridContainer.model.get(name) : null;
		const item = this._itemSources.get(uid);
		if (!model || !item || (model.properties as Record<string, unknown>)?.error) return;

		const current = model.getSourceData() as GridSource;
		this._committing = true;
		try {
			if (item.freeForm) {
				const source = sanitize(current) as Record<string, any>;
				// runtime-only keys - the identity lives in the item, not in the model
				delete source.name;
				delete source.index;
				delete source.props;
				this.adapter.updateSheet(uid, {sheet_model: source});
			} else {
				const base = this._baseSources.get(uid);
				if (!base) return;
				this.adapter.updateSheet(uid, {sheet_diff: createSheetDiff(current, base)});
			}
		} finally {
			this._committing = false;
		}
		this.dispatchEvent(new CustomEvent('edit', {bubbles: true, composed: true}));
	}

	/** flush all pending debounced commits (called by the container before save) */
	commitEdits() {
		[...this._pendingCommits.keys()].forEach(uid => {
			clearTimeout(this._pendingCommits.get(uid));
			this._pendingCommits.delete(uid);
			this.commitSheet(uid);
		});
	}

	// -- tabs -----------------------------------------------------------------

	/** og-gridgroup already added an empty model - mirror it into the store */
	private _onTabAdd = () => {
		setTimeout(() => {
			const models = this._editor.gridContainer.model;
			for (const model of models.models.values()) {
				if (this._uidByName.has(model.name)) continue;
				this._committing = true;
				try {
					const uid = this.adapter.addFreeFormItem(model.name);
					if (!uid) return;
					this._uidByName.set(model.name, uid);
					this._itemSources.set(uid, {
						uid, name: model.name, item: this.store?.getItem(uid),
						index: this.store?.indexOf(uid), freeForm: true,
					});
				} finally {
					this._committing = false;
				}
				this.dispatchEvent(new CustomEvent('edit', {bubbles: true, composed: true}));
			}
		});
	}

	private _onTabMove = () => {
		this._committing = true;
		try {
			this.adapter.updateLayout(sanitize(this._editor.gridContainer.getSourceData().layout) as Record<string, any>);
		} finally {
			this._committing = false;
		}
		this.dispatchEvent(new CustomEvent('edit', {bubbles: true, composed: true}));
	}

	/**
	 * Take over the editor tab menu: the built-in duplicate/delete would bypass
	 * the store, so the menu is rebuilt with store-backed actions on every open
	 * (keys with the `r:` prefix are ignored by the editor's own handler).
	 */
	private _onTabContextMenu = (e: Event) => {
		const tab = (e as TabEvent).tab;
		const uid = this._uidByName.get(tab?.id as string);
		const item = uid ? this._itemSources.get(uid) : null;
		if (!item) return;

		const menu = this._editor.tabMenu;
		if (!menu) return;
		if (!(menu as unknown as {_rpPatched?: boolean})._rpPatched) {
			(menu as unknown as {_rpPatched?: boolean})._rpPatched = true;
			menu.addEventListener('select', this._onTabMenuSelect);
		}

		menu.items = [
			...(!item.freeForm ? [{key: 'r:filters', value: msg('Filter'), icon: 'filter'}] : []),
			{key: 'r:rename', value: msg('Rename'), icon: 'pen'},
			{key: 'properties', value: msg('Properties'), icon: 'gear'},
			{key: 'r:duplicate', value: msg('Duplicate'), icon: 'copy'},
			{key: 'r:delete', value: msg('Delete'), icon: 'trash'},
		] as MenuItem[];
	}

	private _onTabMenuSelect = async (e: Event) => {
		const menu = this._editor.tabMenu;
		const key = menu.items[(e as ListSelectEvent).index]?.key as string;
		if (!key?.startsWith('r:')) return;

		const name = (this._editor as unknown as {activeMenuSheet: string}).activeMenuSheet;
		const uid = this._uidByName.get(name);
		const item = uid ? this._itemSources.get(uid) : null;
		if (!item) return;

		switch (key) {
			case 'r:filters':
				this.adapter.onAction('filter', {...item, index: this.store?.indexOf(uid) ?? item.index});
				break;
			case 'r:rename': {
				const cmd = await rp.host.openMenu({
					mode: 'input',
					value: item.item?.title ?? name,
					text: msg('Rename sheet'),
					content: [{id: 'rename', name: msg('Rename')}],
				});
				if (cmd?.id != null && cmd.value) this.adapter.renameItem(uid, cmd.value);
				break;
			}
			case 'r:duplicate':
				this.commitEdits();
				this.adapter.duplicateItem(uid);
				break;
			case 'r:delete':
				this.adapter.removeItem(uid);
				break;
		}
	}

	/** activate the sheet tab of the given item (e.g. from the sidebar filter item selector) */
	activateSheet(uid: string) {
		const name = this.sheetNameOf(uid);
		if (name) this._editor?.gridContainer?.activateGrid(name);
	}

	/** re-query the report data of one sheet (e.g. after a linked item filter change) */
	async refreshItem(uid: string) {
		const item = this.adapter.getItems().find(i => i.uid === uid);
		if (item && !item.freeForm) await this.reloadSheet(item);
	}

	// -- container integration ------------------------------------------------

	getOptions(): VisualizationOptions {
		return this.createOptions({
			addTo: [],
			exportTypes: [{type: 'xlsx', icon: 'file-xls'}],
			addMenu: [
				{id: 'sheet', text: msg('Add sheet'), icon: 'table'},
				{id: 'savedView', text: msg('Add saved view'), icon: 'bookmark'},
			],
		});
	}

	async export(type: string): Promise<any> {
		if (type !== 'xlsx') return null;
		this.commitEdits();
		const models = this._editor.gridContainer.gridModels.map((model: GridModel) => Object.assign(model, {
			exportOptions: {defaultBorders: !!model.options.grid},
		}));
		return createWorkbook(models, {
			exportHiddenRows: true,
			exportHiddenColumns: true,
			gridLines: false,
			defaultBorders: true,
		});
	}

	async download(type: string) {
		if (type !== 'xlsx') return;
		const workbook = await this.export(type);
		const name = (this.store?.context?.name || 'spreadsheet').replace(/[\\/:*?"<>|]/g, '');
		await exportXLSX(workbook, `${name}.xlsx`);
	}

}
